/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import {
  AmountString,
  Amounts,
  DecimalNumber,
  HttpStatusCode,
  RoundingMode,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  Attention,
  Loading,
  RenderAmount,
  RouteDefinition,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";

import { CurrencySpecification } from "@gnu-taler/taler-util";
import {
  revalidateConversionRateClasses,
  useConversionInfo,
  useConversionRateClasses,
} from "../../hooks/regional.js";
import { RetryableError } from "../../components/RetryableError.js";

interface Props {
  routeCreate: RouteDefinition;
  routeShowDetails: RouteDefinition<{ classId: string }>;
  routeDefault: RouteDefinition;
}

export function ConversionClassList({
  routeCreate,
  routeShowDetails,
  routeDefault,
}: Props): VNode {
  const result = useConversionRateClasses();
  const { i18n } = useTranslationContext();
  const resultInfo = useConversionInfo();

  const convInfo =
    !resultInfo || resultInfo instanceof Error || resultInfo.type === "fail"
      ? undefined
      : resultInfo.body;

  if (!convInfo) {
    return <Fragment>-</Fragment>;
  }
  if (!result) {
    return <Loading />;
  }
  if (result.tag === "error") {
    const failure = result.error;
    if (failure instanceof TalerError) {
      return (
        <RetryableError
          error={failure}
          title={i18n.str`Failed to load conversion rate.`}
          onRetry={() => void revalidateConversionRateClasses()}
        />
      );
    }
    switch (failure.case) {
      case HttpStatusCode.Forbidden:
        return (
          <Attention
            type="warning"
            title={i18n.str`Not enough permission to access the conversion rate list.`}
          ></Attention>
        );
      case HttpStatusCode.NotFound:
        return (
          <Attention
            type="warning"
            title={i18n.str`The conversion-rate list was not found. Conversion may not be supported.`}
          ></Attention>
        );
      case HttpStatusCode.NotImplemented:
        return (
          <Attention
            type="warning"
            title={i18n.str`Conversion list not implemented.`}
          ></Attention>
        );
      case HttpStatusCode.Unauthorized:
        return (
          <Attention
            type="warning"
            title={i18n.str`Not enough permission to access the conversion rate list.`}
          ></Attention>
        );
      default:
        assertUnreachable(failure);
    }
  }

  const page = result.value;
  const classes = page.items;

  return (
    <Fragment>
      <div class="mt-2">
        <div class="sm:flex sm:items-center">
          <div class="sm:flex-auto">
            <h1 class="text-2xl font-semibold text-brand">
              <i18n.Translate>Conversion rate classes</i18n.Translate>
            </h1>
            <p class="mt-2 text-sm text-gray-600">
              <i18n.Translate>
                Configure different cash-in and cashout terms for groups of
                accounts.
              </i18n.Translate>
            </p>
          </div>
          <div class="mt-4 flex flex-wrap gap-3 sm:ml-16 sm:mt-0 sm:flex-none">
            <a
              href={routeDefault.url({})}
              class="block rounded-md bg-white px-3 py-2 text-center text-sm font-semibold text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              <i18n.Translate>Default conversion rate</i18n.Translate>
            </a>
            <a
              href={routeCreate.url({})}
              data-name="create account"
              class="block rounded-md bg-primary px-3 py-2 text-center text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <i18n.Translate>Create conversion rate class</i18n.Translate>
            </a>
          </div>
        </div>
        <div class="mt-6 flow-root">
          <div class="overflow-x-auto">
            <div class="inline-block min-w-full py-2 align-middle">
              {!classes.length ? (
                <div class="rounded-lg border border-onBackground/10 bg-white p-6 text-sm text-gray-600">
                  <div class="font-semibold text-onBackground">
                    <i18n.Translate>No conversion rate classes</i18n.Translate>
                  </div>
                  <p class="mt-1">
                    <i18n.Translate>
                      All accounts currently use the default conversion rate.
                    </i18n.Translate>
                  </p>
                </div>
              ) : (
                <table class="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-onBackground sm:pl-0"
                      >{i18n.str`Name`}</th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-onBackground sm:pl-0"
                      >{i18n.str`Description`}</th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-onBackground sm:pl-0"
                      >{i18n.str`Cashin`}</th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-onBackground"
                      >{i18n.str`Cashout`}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    {classes.map((row) => {
                      return (
                        <tr key={row.conversion_rate_class_id} class="">
                          <td class="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-onBackground sm:pl-0">
                            <a
                              href={routeShowDetails.url({
                                classId: String(row.conversion_rate_class_id),
                              })}
                            >
                              {row.name}
                            </a>
                          </td>
                          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <a
                              href={routeShowDetails.url({
                                classId: String(row.conversion_rate_class_id),
                              })}
                            >
                              {row.description}
                            </a>
                          </td>
                          <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            <a
                              href={routeShowDetails.url({
                                classId: String(row.conversion_rate_class_id),
                              })}
                            >
                              <DescribeConversion
                                ratio={
                                  row.cashin_ratio ??
                                  convInfo.conversion_rate.cashin_ratio
                                }
                                fee={
                                  row.cashin_fee ??
                                  convInfo.conversion_rate.cashin_fee
                                }
                                min={
                                  row.cashin_min_amount ??
                                  convInfo.conversion_rate.cashin_min_amount
                                }
                                rounding={
                                  row.cashin_rounding_mode ??
                                  convInfo.conversion_rate.cashin_rounding_mode
                                }
                                minSpec={convInfo.fiat_currency_specification}
                                feeSpec={
                                  convInfo.regional_currency_specification
                                }
                              />
                            </a>
                          </td>
                          <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            <a
                              href={routeShowDetails.url({
                                classId: String(row.conversion_rate_class_id),
                              })}
                            >
                              <DescribeConversion
                                ratio={
                                  row.cashout_ratio ??
                                  convInfo.conversion_rate.cashout_ratio
                                }
                                fee={
                                  row.cashout_fee ??
                                  convInfo.conversion_rate.cashout_fee
                                }
                                min={
                                  row.cashout_min_amount ??
                                  convInfo.conversion_rate.cashout_min_amount
                                }
                                rounding={
                                  row.cashout_rounding_mode ??
                                  convInfo.conversion_rate.cashout_rounding_mode
                                }
                                minSpec={
                                  convInfo.regional_currency_specification
                                }
                                feeSpec={convInfo.fiat_currency_specification}
                              />
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {page.loadFirst || page.loadNext ? (
              <nav
                class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg"
                aria-label={i18n.str`Pagination`}
              >
                <div class="flex flex-1 justify-between sm:justify-end">
                  <button
                    type="button"
                    name="first page"
                    class="relative disabled:bg-gray-100 disabled:text-gray-500 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-onBackground ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
                    disabled={!page.loadFirst}
                    onClick={page.loadFirst}
                  >
                    <i18n.Translate>First page</i18n.Translate>
                  </button>
                  <button
                    type="button"
                    name="next page"
                    class="relative disabled:bg-gray-100 disabled:text-gray-500 ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-onBackground ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
                    disabled={!page.loadNext}
                    onClick={page.loadNext}
                  >
                    <i18n.Translate>Next</i18n.Translate>
                  </button>
                </div>
              </nav>
            ) : undefined}
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export function DescribeConversion({
  fee,
  min,
  ratio,
  rounding,
  feeSpec,
  minSpec,
}: {
  min: AmountString;
  ratio: DecimalNumber;
  fee: AmountString;
  rounding: RoundingMode;
  minSpec: CurrencySpecification;
  feeSpec: CurrencySpecification;
}): VNode {
  const { i18n } = useTranslationContext();

  return (
    <Fragment>
      1:{ratio}
      {Amounts.isZero(min) ? undefined : (
        <Fragment>
          <br />
          <i18n.Translate>min:</i18n.Translate>&nbsp;
          <RenderAmount spec={minSpec} value={Amounts.parseOrThrow(min)} />
        </Fragment>
      )}
      {Amounts.isZero(fee) ? undefined : (
        <Fragment>
          <br />
          <i18n.Translate>fee:</i18n.Translate>&nbsp;
          <RenderAmount spec={feeSpec} value={Amounts.parseOrThrow(fee)} />
        </Fragment>
      )}
    </Fragment>
  );
}
